<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import {
    refDebounced,
    useDocumentVisibility,
    useIntervalFn,
    useNow,
  } from '@vueuse/core';
  import { useReservationAgenda } from '@/composition/reservations/use-reservation-agenda';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import V2StatsExportButton from '@/components/v2/stats/V2StatsExportButton.vue';
  import { reservationsAgendaExportRows } from '@/domain/reservations/reservation-export';
  import { useUi } from '@/composition/use-ui';
  import {
    addDays,
    getReservationTimeZone,
    toDateKey,
  } from '@/domain/reservations/reservation-time';
  import type {
    IReservation,
    TReservationStatus,
  } from '@/interfaces/IReservation';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useReservationsStore } from '@/stores/reservations.store';
  import V2ReservationsAgendaDayGroup from './V2ReservationsAgendaDayGroup.vue';
  /** Covers any booking horizon, so the badge count and the list agree. */
  const PENDING_HORIZON_DAYS = 365;
  const props = defineProps<{
    /** Start on the pending requests instead of this week. */
    pendingFirst?: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'select', reservation: IReservation): void;
    (event: 'create'): void;
  }>();
  const restaurant = useRestaurantStore();
  const store = useReservationsStore();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const now = useNow({ interval: 30_000 });
  const visibility = useDocumentVisibility();
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  // Opened from the pending badge: list every request that still needs an answer.
  const preset = ref(props.pendingFirst ? 'custom' : 'thisWeek');
  const search = ref('');
  const query = refDebounced(search, 200);
  const onlineOnly = ref(false);
  const status = ref<TReservationStatus | 'all'>(
    props.pendingFirst ? 'pending' : 'all'
  );
  const from = ref(toDateKey(now.value, zone.value));
  const to = ref(
    addDays(from.value, props.pendingFirst ? PENDING_HORIZON_DAYS : 6)
  );
  const statuses: (TReservationStatus | 'all')[] = [
    'all',
    'pending',
    'pending_payment',
    'confirmed',
    'arrived',
    'seated',
    'finished',
    'cancelled',
    'no_show',
  ];
  const presets = computed(() =>
    ['thisWeek', 'next7', 'thisMonth', 'custom'].map((id) => ({
      id,
      label: i18nT(`reservations.agenda.${id}`),
    }))
  );
  const validDates = computed(() => {
    try {
      return addDays(from.value, 0) <= addDays(to.value, 0);
    } catch {
      return false;
    }
  });
  watch([preset, zone], setPreset, { immediate: true });
  // A deep link mounts before the pending count arrives; seed once, only while untouched.
  watch(
    () => props.pendingFirst,
    (on) => {
      if (
        !on ||
        preset.value !== 'thisWeek' ||
        status.value !== 'all' ||
        search.value
      )
        return;
      status.value = 'pending';
      preset.value = 'custom';
      to.value = addDays(from.value, PENDING_HORIZON_DAYS);
    }
  );
  const { rows, total, loading, failed, load } = useReservationAgenda(
    () => {
      const restaurantId = restaurant.restaurant?.id;
      const branchId = restaurant.selectedBranch?.id;
      return restaurantId && branchId && validDates.value
        ? {
            restaurantId,
            branchId,
            from: from.value,
            to: to.value,
            filters: {
              status: status.value === 'all' ? undefined : status.value,
              source: onlineOnly.value ? 'online' : undefined,
              search: query.value,
            },
          }
        : null;
    },
    (error) => ui.serverError(error, i18nT('reservations.page.loadError'))
  );
  const exportData = computed(() =>
    reservationsAgendaExportRows(rows.value, i18nT, zone.value)
  );
  const groups = computed(() => {
    const dates = new Map<string, IReservation[]>();
    for (const row of rows.value) {
      const list = dates.get(row.serviceDate) ?? [];
      list.push(row);
      dates.set(row.serviceDate, list);
    }
    return [...dates]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, reservations]) => ({ date, reservations }));
  });
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }
  function setPreset() {
    if (preset.value === 'custom') return;
    const today = toDateKey(now.value, zone.value);
    const date = new Date(`${today}T12:00:00Z`);
    if (preset.value === 'thisWeek') {
      from.value = addDays(today, -((date.getUTCDay() + 6) % 7));
      to.value = addDays(from.value, 6);
    } else if (preset.value === 'next7') {
      from.value = today;
      to.value = addDays(today, 6);
    } else {
      from.value = `${today.slice(0, 7)}-01`;
      const end = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
      );
      to.value = end.toISOString().slice(0, 10);
    }
  }
  function dateChanged(which: 'from' | 'to', value: string) {
    preset.value = 'custom';
    if (which === 'from') from.value = value;
    else to.value = value;
  }
  watch(
    () => store.revision,
    () => {
      if (visibility.value === 'visible') void load();
    }
  );
  useIntervalFn(() => {
    if (visibility.value === 'visible') void load();
  }, 60_000);
</script>
<template>
  <div class="reservations-agenda">
    <div class="agenda-card">
      <div class="agenda-toolbar">
        <div class="agenda-command-row">
          <PosUiSegmented
            :value="preset"
            :items="presets"
            @input="preset = $event"
          />
          <V2StatsExportButton
            v-bind="exportData"
            class="agenda-export"
            :filename="`reservas-${from}-${to}`"
            :sheet-name="$t('reservations.export.agenda')"
            :disabled="loading || !validDates"
          />
        </div>
        <div class="agenda-inputs">
          <PosUiField
            :label="$t('reservations.agenda.from')"
            input-id="rsv-agenda-from"
            ><PosUiInput
              id="rsv-agenda-from"
              :value="from"
              type="date"
              @input="dateChanged('from', $event)" /></PosUiField
          ><PosUiField
            :label="$t('reservations.agenda.to')"
            input-id="rsv-agenda-to"
            ><PosUiInput
              id="rsv-agenda-to"
              :value="to"
              type="date"
              :min="from"
              @input="dateChanged('to', $event)" /></PosUiField
          ><PosUiInput
            v-model="search"
            class="agenda-search"
            icon="magnify"
            clearable
            :placeholder="$t('reservations.bar.searchPlaceholder')"
          /><PosUiSwitch
            v-model="onlineOnly"
            :label="$t('reservations.agenda.onlineOnly')"
          />
        </div>
      </div>
      <PosUiScrollable orientation="horizontal" arrows hide-scrollbar="touch"
        ><div class="agenda-filters">
          <PosUiChip
            v-for="value in statuses"
            :key="value"
            :active="status === value"
            @click="status = value"
            >{{
              $t(
                value === 'all'
                  ? 'reservations.filter.all'
                  : `reservations.status.${value}`
              )
            }}</PosUiChip
          >
        </div></PosUiScrollable
      >
      <div class="agenda-content">
        <div v-if="loading && !rows.length" aria-busy="true">
          <PosUiSkeleton v-for="index in 6" :key="index" class="row-skeleton" />
        </div>
        <PosUiEmptyState
          v-else-if="failed && !rows.length"
          icon="alert-circle-outline"
          :title="$t('reservations.page.loadError')"
          ><template #action
            ><PosUiButton icon="refresh" @click="load()">{{
              $t('reservations.page.retry')
            }}</PosUiButton></template
          ></PosUiEmptyState
        >
        <PosUiEmptyState
          v-else-if="!rows.length"
          icon="calendar-month-outline"
          :title="$t('reservations.agenda.emptyTitle')"
          :description="$t('reservations.list.emptyDescription')"
          ><template #action
            ><PosUiButton
              icon="plus"
              color="neutral"
              variant="outline"
              @click="emit('create')"
              >{{ $t('reservations.bar.new') }}</PosUiButton
            ></template
          ></PosUiEmptyState
        >
        <template v-else
          ><V2ReservationsAgendaDayGroup
            v-for="group in groups"
            :key="group.date"
            v-bind="group"
            :now="now"
            :time-zone="zone"
            :tolerance-min="store.day?.settingsSummary.toleranceMin ?? 15"
            :business-day-start-hour="
              restaurant.selectedBranch?.businessDayStartHour
            "
            :selected-id="store.selectedReservationId"
            @select="emit('select', $event)"
          /><PosUiButton
            v-if="failed"
            color="neutral"
            variant="outline"
            @click="load()"
            >{{ $t('reservations.page.retry') }}</PosUiButton
          ><PosUiButton
            v-else-if="rows.length < total"
            :disabled="loading"
            color="neutral"
            variant="outline"
            @click="load(true)"
            >{{ $t('reservations.agenda.loadMore') }}</PosUiButton
          ></template
        >
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
  // Content page: a padded, centred white card (see the V2 layout rules).
  .reservations-agenda {
    @apply tw-mx-auto tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-7xl tw-flex-col tw-p-4 md:tw-p-6;
    .agenda-card {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
    .agenda-toolbar {
      @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3;
    }
    .agenda-command-row {
      @apply tw-flex tw-flex-nowrap tw-items-center tw-gap-3;
      // On phones the presets wrap inside the control instead of pushing the export down.
      :deep(.pos-ui-segmented) {
        @apply tw-min-w-0 tw-flex-1;
      }
      :deep(.pos-ui-segmented-option) {
        @apply tw-min-h-11 tw-h-auto tw-whitespace-normal tw-px-2 tw-py-2;
      }
      .agenda-export {
        @apply tw-flex-none;
      }
    }
    .agenda-inputs {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-items-end tw-gap-3;
      > * {
        @apply tw-min-w-0;
      }
    }
    .agenda-search {
      @apply tw-min-w-[12rem] tw-flex-1;
    }
    .agenda-filters {
      @apply tw-flex tw-gap-2 tw-px-3 tw-py-2;
    }
    // Day groups are white cards: the well behind them is the canvas tone.
    .agenda-content {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-bg-canvas tw-p-3 md:tw-p-4;
    }
    .row-skeleton {
      @apply tw-mb-3 tw-h-14 tw-w-full;
    }
  }
</style>
